import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Home,
  Package,
  Calendar,
  Settings,
  Menu,
  X,
  Plus,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "../../lib/utils";
import { ROUTES } from "../../config/routes";
import { PropertySwitcher } from "../PropertySwitcher";
import { UserMenu } from "../UserMenu";
import { Logo } from "../Logo";
import { useProperty } from "../../contexts/PropertyContext";
import NotificationsDropdown from "./NotificationsDropdown";

interface NavItem {
  name: string;
  href: string;
  icon: React.ElementType;
  /** serviceType to check — undefined means always visible (owner + members) */
  serviceType?: string;
}

export function Navbar() {
  const { t } = useTranslation('nav');
  const location = useLocation();
  const navigate = useNavigate();
  const { properties, hasServiceAccess } = useProperty();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [propertyDropdownOpen, setPropertyDropdownOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  const navigationItems: NavItem[] = [
    { name: t('home'),        href: ROUTES.DASHBOARD,   icon: Home },
    { name: t('assets'),      href: ROUTES.ASSETS,      icon: Package },
    { name: t('maintenance'), href: ROUTES.MAINTENANCE, icon: Calendar, serviceType: "maintenance" },
  ];

  const hasNoProperties = properties.length === 0;

  // Filter nav items based on current property's granted service access
  // (hasServiceAccess returns true for new users with no properties)
  const visibleNavItems = navigationItems.filter((item) =>
    item.serviceType === undefined || hasServiceAccess(item.serviceType)
  );

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  return (
    <>
      <nav
        className={cn(
          "fixed top-0 left-0 right-0 z-50 transition-all duration-300",
          "bg-background/95 backdrop-blur-xl border-b",
          scrolled
            ? "shadow-md"
            : "shadow-sm"
        )}
      >
        <div className="mx-auto max-w-7xl px-4 lg:px-8">
          <div className="flex h-14 items-center justify-between rtl:flex-row-reverse">
            {/* Logo */}
            <Link
              to="/"
              className="flex items-center gap-2 group"
            >
              <Logo />
            </Link>

            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-0.5">
              {visibleNavItems.map((item) => {
                const isActive =
                  location.pathname === item.href ||
                  location.pathname.startsWith(item.href + "/");
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={cn(
                      "relative px-3 py-1.5 text-sm font-medium rounded-full transition-all duration-300",
                      isActive
                        ? "text-primary-foreground bg-primary"
                        : "text-muted-foreground hover:text-foreground hover:bg-secondary"
                    )}
                  >
                    {item.name}
                  </Link>
                );
              })}
            </div>

            {/* Right side */}
            <div className="flex items-center gap-2">
              {/* Add First House Button (if no properties exist) */}
              {hasNoProperties && (
                <button
                  onClick={() => navigate('/profile', { state: { openAddModal: true } })}
                  className={cn(
                    "hidden md:flex items-center gap-2 px-4 py-2 rounded-lg",
                    "bg-primary text-primary-foreground text-sm font-medium",
                    "hover:bg-primary/90 transition-all duration-200",
                    "shadow-md hover:shadow-lg"
                  )}
                  id="add-first-house-button"
                >
                  <Plus className="h-4 w-4" />
                  {t('addFirstHouse')}
                </button>
              )}

              {/* Property Switcher */}
              {!hasNoProperties && (
                <div className="hidden md:block">
                  <PropertySwitcher 
                    isOpen={propertyDropdownOpen}
                    setIsOpen={(open) => {
                      setPropertyDropdownOpen(open);
                      if (open) setUserMenuOpen(false);
                    }}
                  />
                </div>
              )}

              {/* User Menu */}
              <div className="hidden md:block">
                <UserMenu 
                  isOpen={userMenuOpen}
                  setIsOpen={(open) => {
                    setUserMenuOpen(open);
                    if (open) setPropertyDropdownOpen(false);
                  }}
                />
              </div>

              {/* Notifications — last on the right */}
              <div className="hidden md:block">
                <NotificationsDropdown scrolled={scrolled} />
              </div>

              {/* Mobile Hamburger */}
              <button
                onClick={() => setMobileOpen(!mobileOpen)}
                className="md:hidden flex items-center justify-center h-9 w-9 rounded-full border hover:bg-secondary transition-colors duration-200"
              >
                {mobileOpen ? (
                  <X className="h-4 w-4" />
                ) : (
                  <Menu className="h-4 w-4" />
                )}
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      <div
        className={cn(
          "fixed inset-0 z-40 bg-background/95 backdrop-blur-lg transition-all duration-300 md:hidden",
          mobileOpen
            ? "opacity-100 pointer-events-auto"
            : "opacity-0 pointer-events-none"
        )}
      >
        <div className="flex flex-col items-center justify-center h-full gap-6">
          {visibleNavItems.map((item, i) => {
            const Icon = item.icon;
            const isActive =
              location.pathname === item.href ||
              location.pathname.startsWith(item.href + "/");
            return (
              <Link
                key={item.name}
                to={item.href}
                className={cn(
                  "flex items-center gap-3 text-2xl font-medium transition-all duration-300",
                  mobileOpen
                    ? "opacity-100 translate-y-0"
                    : "opacity-0 translate-y-4",
                  isActive ? "text-foreground" : "text-muted-foreground"
                )}
                style={{ transitionDelay: `${i * 60}ms` }}
              >
                <Icon className="h-6 w-6" />
                {item.name}
              </Link>
            );
          })}
          <Link
            to={ROUTES.SETTINGS}
            className={cn(
              "flex items-center gap-3 text-2xl font-medium text-muted-foreground transition-all duration-300",
              mobileOpen
                ? "opacity-100 translate-y-0"
                : "opacity-0 translate-y-4"
            )}
            style={{ transitionDelay: `${visibleNavItems.length * 60}ms` }}
          >
            <Settings className="h-6 w-6" />
            {t('settings')}
          </Link>        </div>
      </div>
    </>
  );
}
